import { Component, computed, ElementRef, Input, OnInit, Signal, ViewChild } from '@angular/core';
import { FloorPlanInfo, Place } from 'src/app/interfaces/interfaces';
import { StaticCanvas, Group, FabricImage, FabricText } from 'fabric'
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { UserStore } from 'src/app/stores/user.store';
import { PlacesStore } from 'src/app/stores/places.store';

@Component({
  selector: 'app-add-favorite-maps',
  templateUrl: './add-favorite-maps.component.html',
  styleUrls: ['./add-favorite-maps.component.scss'],
})
export class AddFavoriteMapsComponent  implements OnInit {
  @ViewChild('floorCanvasPreview', { static: false }) floorCanvasPreview!: ElementRef;
  @Input() floorplan: FloorPlanInfo;
  canvas: any;
  isLoading = false
  linkedResourcesCount = 0
  linkedResources : string[]
  linkedResourcesAsPlaces : Signal<Place[]> = computed(() => this.placesStore.places().filter(item=> this.linkedResources.includes(item.emailAddress))) 
  constructor(private placesStore: PlacesStore,private toastService: ToastService,private userStore: UserStore) {

   }

  ngOnInit() {
    if(!this.floorplan.floorMapImage.startsWith("data")) {
      this.linkedResourcesCount = JSON.parse(this.floorplan.floorMapImage).objects.length - 1
      this.linkedResources = JSON.parse(this.floorplan.floorMapImage).objects.map(obj => obj.name)
      this.linkedResources.shift() //to remove the image from the list:/
    }

  }


  toggleFavorite(map:FloorPlanInfo) {
    this.isLoading = true
    this.userStore.toggleMapFavorite(map._id).then(() => {
      this.isLoading = false
    }).catch(() => {
      this.isLoading = false
      this.toggleFavMapWentWrongToast()
    })
  }

  openMapPreview() {
    setTimeout(() => {
      if(this.floorplan.floorMapImage.startsWith("data")) {
        this.initAndLoadNormalImage()
      } else {
        this.initAndDrawCanvasWithAvailibility();
      }
    }, 100);
  }


  private initAndLoadNormalImage() {
    this.canvas = new StaticCanvas(this.floorCanvasPreview.nativeElement);

    // In Fabric.js v6, fromURL returns a Promise
    FabricImage.fromURL(this.floorplan.floorMapImage).then((object) => {
      this.canvas.setWidth(300);
      this.canvas.setHeight(200);
      const scaleX = this.canvas.width / object.width;
      const scaleY = this.canvas.height / object.height;
      const scaleFactor = Math.min(scaleX, scaleY); // Set the viewport properties
      this.canvas.setViewportTransform([
        scaleFactor,
        0,
        0,
        scaleFactor ,
        (this.canvas.width - object.width * scaleFactor) ,
        (this.canvas.height - object.height * scaleFactor) ,
      ]);

      object.set({
        left: this.canvas.width / 2,
        top: this.canvas.height / 2,
        originX: 'center',
        originY: 'center',
        hasControls: false,
        selectable: false,
        name : "planImage"
      });
      this.canvas.add(object);
      this.canvas.viewportCenterObject(object);
      this.canvas.requestRenderAll();
    });
  }
  
  private async initAndDrawCanvasWithAvailibility() {
    try {
      const json = JSON.parse(this.floorplan.floorMapImage);
      json.version = '6.0.0';
  
      // Set the dimensions of the canvas
      this.canvas = new StaticCanvas(this.floorCanvasPreview.nativeElement, {
        width: 300,
        height: 200,
        backgroundColor: '#ffffff' // Ensure background is visible
      });
  
      // modern v6 approach
      await this.canvas.loadFromJSON(json);
      
      const objects = this.canvas.getObjects();
      console.log('Loaded objects:', objects);
      
      if (objects.length === 0) {
        throw new Error('No objects loaded - check your JSON structure');
      }
  
      // Calculate bounding box
      const boundingRect = this.calculateBoundingBox(objects);
      const boundingWidth = boundingRect.right - boundingRect.left;
      const boundingHeight = boundingRect.bottom - boundingRect.top;
  
      //Calculate scaling and positioning
      const scaleFactorWidth = this.canvas.getWidth() / boundingWidth;
      const scaleFactorHeight = this.canvas.getHeight() / boundingHeight;
      const scaleFactor = Math.min(scaleFactorWidth, scaleFactorHeight);
      
      const offsetX = (this.canvas.getWidth() - boundingWidth * scaleFactor) / 2;
      const offsetY = (this.canvas.getHeight() - boundingHeight * scaleFactor) / 2;
  
      //Transform objects
      objects.forEach(obj => {
        obj.scaleX *= scaleFactor;
        obj.scaleY *= scaleFactor;
        obj.left = (obj.left - boundingRect.left) * scaleFactor + offsetX;
        obj.top = (obj.top - boundingRect.top) * scaleFactor + offsetY;
        obj.setCoords();
        this.colorShapesBasedOnAvailibility(obj);
      });
      this.canvas.requestRenderAll();
    } catch (error) {
      console.error('Canvas initialization failed:', error);
    }
  }
  
  private calculateBoundingBox(objects:any) {
    return objects.reduce((acc, obj) => {
      const objRect = obj.getBoundingRect();
      return {
        left: Math.min(acc.left, objRect.left),
        top: Math.min(acc.top, objRect.top),
        right: Math.max(acc.right, objRect.left + objRect.width),
        bottom: Math.max(acc.bottom, objRect.top + objRect.height)
      };
    }, { 
      left: Infinity, 
      top: Infinity, 
      right: -Infinity, 
      bottom: -Infinity 
    });
  }
  
  // Updated coloring function for v6
  private colorShapesBasedOnAvailibility(obj:any) {
    const place = this.linkedResourcesAsPlaces().find(
      (item: any) => item.emailAddress === (obj as any).name
    );  
    if (!place) {
      obj.set({
        fill: 'rgba(0,0,0,0.5)',
        stroke: '#000',
        strokeWidth: 1
      });
      return;
    }
  
    const isAvailable = place.isAvailable;
    const colorSettings = isAvailable ? {
      fill: "rgba(40,176,11,0.2)",
      stroke: "rgba(40,176,11,1)",
      strokeWidth: 4
    } : {
      fill: "rgba(224,6,28,0.2)",
      stroke: "rgba(224,6,28,1)",
      strokeWidth: 4
    };
  
    // Handle groups differently
    if (obj.type === 'group') {
      const group = obj as Group;
      group.getObjects().forEach(subObj => {
        subObj.set(colorSettings);
      });
    } else {
      obj.set(colorSettings);
      this.addCapacityLabel(obj, place, isAvailable);
    }
  }
  
  private addCapacityLabel(obj: any, place: any, isAvailable: boolean) {
    let capacityText = '';
    
    if (place.type === "room") {
      capacityText = place?.capacity?.toString() || '-';
    } else {
      capacityText = `${place?.availableSpace ?? '-'}/${place?.capacity ?? '-'}`;
    }
  
    const text = new FabricText(capacityText, {
      fill: isAvailable ? "rgba(40,176,11,1)" : "rgba(224,6,28,1)",
      originX: "center",
      originY: "center",
      fontSize: 12, // Start smaller - will scale
      fontFamily: "Open Sans",
      selectable: false
    });
  
    // Scale text to object size
    const scaleFactor = Math.min(obj.scaleX || 1, obj.scaleY || 1);
    text.set({
      fontSize: Math.max(8, 12 * scaleFactor) // Minimum 8px
    });
  
    // Position text
    const center = obj.getCenterPoint();
    text.setPositionByOrigin(center, 'center', 'center');
    
    this.canvas.add(text);
    this.canvas.bringToFront(text);
  }


  async toggleFavMapWentWrongToast() { 
    this.isLoading = false
    this.toastService.openErrorToastAsync('toggleFavMapWentWrongToast')

  } 

}
